import { useLanguage } from '../context/LanguageContext';
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, Clock, ArrowLeft, Tag } from 'lucide-react';
import { publicApi } from '../context/AuthContext';
import DOMPurify from 'dompurify';
import useSEO from '../hooks/useSEO';
import StructuredData, { buildBlogPostingSchema } from '../components/StructuredData';

export default function BlogPost() {
  const { t, language } = useLanguage();
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPost();
    window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, language]);

  const fetchPost = async () => {
    try {
      const langParam = language !== 'fr' ? `?lang=${language}` : '';
      const { data } = await publicApi.get(`/blog/posts/${slug}${langParam}`);
      setPost(data);
      
      // Fetch related posts in same category
      const relatedLang = language !== 'fr' ? `&lang=${language}` : '';
      const { data: related } = await publicApi.get(`/blog/posts?category=${data.category}${relatedLang}`);
      setRelatedPosts(related.filter(p => p.slug !== slug).slice(0, 3));
    } catch (error) {
      console.error('Error fetching post:', error);
    } finally {
      setLoading(false);
    }
  };

  useSEO({
    title: post?.meta_title || post?.title,
    description: post?.meta_description || post?.excerpt,
    ogImage: post?.og_image || post?.image || post?.featured_image,
    url: slug ? `https://joeycyan.com/blog/${slug}` : undefined
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#00aaff] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Article non trouvé</h1>
          <Link to="/blog" className="text-[#00aaff] hover:underline">
            {t('blogPost.backToBlog')}
          </Link>
        </div>
      </div>
    );
  }

  const getCategoryIcon = (category) => {
    const icons = {
      'conseils': '💡',
      'technologie': '⚡',
      'actualités': '📰',
      'actualites': '📰',
      'style': '✨',
      'santé visuelle': '👁️',
      'sante-visuelle': '👁️',
      'guide utilisation': '📖',
      'guide-utilisation': '📖'
    };
    return icons[category.toLowerCase()] || '📄';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <StructuredData data={buildBlogPostingSchema(post, { slug })} />
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <Link 
            to="/blog"
            className="inline-flex items-center gap-2 text-gray-600 hover:text-[#00aaff] transition-colors text-sm font-medium mb-4"
          >
            <ArrowLeft size={16} />
            {t('blogPost.backToBlog')}
          </Link>

          {/* Category Badge */}
          <div className="mb-4">
            <span className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#00aaff]/10 text-[#00aaff] text-sm font-bold rounded-full">
              {getCategoryIcon(post.category)}
              {post.category}
            </span>
          </div>

          {/* Title */}
          <h1 className="text-4xl md:text-5xl font-black text-gray-900 mb-4 leading-tight">
            {post.title}
          </h1>

          {/* Meta */}
          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
            {post.reading_time && (
              <div className="flex items-center gap-1.5">
                <Clock size={16} />
                <span>{post.reading_time} min de lecture</span>
              </div>
            )}
            {post.tags && post.tags.length > 0 && (
              <div className="flex items-center gap-1.5">
                <Tag size={16} />
                <span>{post.tags.join(', ')}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Featured Image */}
      {(post.image || post.featured_image) && (
        <div className="w-full h-96 overflow-hidden">
          <img
            src={post.featured_image}
            alt={post.title}
            className="w-full h-full object-cover"
          />
        </div>
      )}

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <article 
          className="prose prose-lg max-w-none
            prose-headings:font-bold prose-headings:text-gray-900
            prose-p:text-gray-700 prose-p:leading-relaxed
            prose-a:text-[#00aaff] prose-a:no-underline hover:prose-a:underline
            prose-strong:text-gray-900 prose-strong:font-bold
            prose-ul:list-disc prose-ol:list-decimal
            prose-img:rounded-xl prose-img:shadow-lg"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content) }}
        />

        {/* CTA */}
        <div className="mt-12 bg-gradient-to-br from-[#00aaff] to-[#0088dd] rounded-2xl p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-3">Prêt à essayer Joey Cyan ?</h3>
          <p className="text-white/90 mb-6">
            Découvrez notre collection de lunettes connectées AR. Livraison express, retours gratuits 30 jours.
          </p>
          <Link
            to="/optique"
            className="inline-block bg-white text-[#00aaff] px-8 py-3 rounded-full font-bold hover:bg-gray-100 transition-colors"
          >
            Voir la collection
          </Link>
        </div>
      </div>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <div className="bg-white border-t border-gray-200 py-12">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">Articles similaires</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedPosts.map((relatedPost) => (
                <Link
                  key={relatedPost.id}
                  to={`/blog/${relatedPost.slug}`}
                  className="group bg-gray-50 rounded-xl overflow-hidden hover:shadow-lg transition-all"
                >
                  <div className="aspect-video overflow-hidden bg-gray-200">
                    <img
                      src={relatedPost.image || relatedPost.featured_image || '/api/uploads/products/placeholder.png'}
                      alt={relatedPost.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="font-bold text-gray-900 group-hover:text-[#00aaff] transition-colors line-clamp-2">
                      {relatedPost.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
